/**
 * Command K — blending the All chip.
 *
 * Pure, like the rest of `models/`: two rules that decide what the All chip
 * shows and in what order, testable under a bare `npx vitest run`.
 */

import { matchItem } from './cmdk-ranking';
import type { CmdkGroupBlock } from './cmdk-rows';
import type { CmdkGroup, CmdkItem, CmdkScope } from './cmdk.model';

/**
 * Under All, a Workplace app and its "Go to" row are one destination twice
 * ("Settings" in Apps, "Go to Settings" in Go to). The app row stays; the
 * Go to chip keeps its own rows.
 */
export function dropWorkspaceDuplicates(items: readonly CmdkItem[], scope: CmdkScope): CmdkItem[] {
  if (scope !== 'all') return [...items];
  const apps = new Set(
    items.flatMap(item => (item.kind === 'app' && item.actionId ? [item.actionId] : [])),
  );
  if (apps.size === 0) return [...items];
  return items.filter(item => !(item.kind === 'nav' && apps.has(item.id)));
}

/**
 * The All chip, blended: groups whose best row is a clearly better match lead
 * ("settings" puts the Settings app above rooms; "spaghetti" puts the room
 * first). Groups are compared by their top row's match TIER, in steps of ten
 * points, so a near-tie keeps the surface's own order — on the map, people,
 * desks and rooms first (ruling M1) — and a group still loading keeps its
 * place instead of jumping when its answer lands. A remote row the palette's
 * matcher cannot read (the server matched it on an amenity, say) counts as a
 * weak match, ahead of a group with nothing.
 */
export function orderGroupsByMatch(
  blocks: readonly CmdkGroupBlock[],
  query: string,
  baseOrder: readonly CmdkGroup[],
): CmdkGroup[] {
  const bucket = new Map<CmdkGroup, number>();
  for (const block of blocks) {
    // A failed source that still has rows (Apps keeps Workplace's own apps
    // when the organisation's links fail) competes on those rows.
    const top = block.items[0];
    if (block.loading || !top) {
      bucket.set(block.group, -1);
      continue;
    }
    const score = matchItem(top, query)?.score ?? 15;
    bucket.set(block.group, Math.floor(score / 10));
  }
  // Loading, failed and absent groups keep their slots; the ranked groups are
  // sorted among the remaining slots. (A comparator mixing the two rules is
  // not a total order.)
  const isRanked = (group: CmdkGroup): boolean => (bucket.get(group) ?? -1) >= 0;
  const position = (group: CmdkGroup): number => baseOrder.indexOf(group);
  const ranked = baseOrder
    .filter(isRanked)
    .sort((a, b) => (bucket.get(b) ?? 0) - (bucket.get(a) ?? 0) || position(a) - position(b));
  let next = 0;
  return baseOrder.map(group => (isRanked(group) ? ranked[next++] : group));
}
